import * as React from 'react';
import useLabelTranslation from 'src/hooks/useLabelTranslation';

import {useDispatch} from 'react-redux';

import {Shimmer} from '@spaced-out/ui-design-system/lib/components/Shimmer';
import {Tooltip} from '@spaced-out/ui-design-system/lib/components/Tooltip';
import {Truncate} from '@spaced-out/ui-design-system/lib/components/Truncate';
import type {ColorTypes} from '@spaced-out/ui-design-system/lib/types/typography';
import {
  BodyMedium,
  SubTitleExtraSmall,
} from '@spaced-out/ui-design-system/lib/components/Text';

import * as reduxApi from 'src/utils/redux-api-v2';

import css from './user.css';


const User = ({
  userId,
  disallowUserInfoTooltip,
  clickable,
  semanticColor,
}: {
  userId: number;
  disallowUserInfoTooltip?: boolean;
  clickable?: boolean;
  semanticColor?: ColorTypes;
}) => {
  const {t} = useLabelTranslation();
  const dispatch = useDispatch();
  const [userData, setUserData] = React.useState<any>(null);
  const [loadingUserDetails, setLoadingUserDetails] = React.useState(false);

  React.useEffect(() => {
    const fetchUser = async () => {
      try {
        setLoadingUserDetails(true);
        const userData = await dispatch(
          reduxApi.post(
            `api/get-users`,
            {
              userIds: [userId],
            },
            {},
            {
              apiPath: '/trmapi/',
            },
          ),
        );
        setUserData(userData[0]);
      } catch (e: any) {
        console.error(e);
      } finally {
        setLoadingUserDetails(false);
      }
    };

    if (userId > 0) {
      fetchUser();
    }
  }, [userId, dispatch]);

  if (loadingUserDetails) {
    return <Shimmer height={14} />;
  }

  if (!userData) {
    return null;
  }
  const displayName = userData.fullName;

  const userTooltipInfo = [
    {value: displayName},
    {label: t('Email', 'Email'), value: userData.username},
    {label: t('Designation', 'Designation'), value: userData.designation ?? ''},
    {label: t('Employee_ID', 'Employee ID'), value: userData.employeeId},
  ];

  if (!disallowUserInfoTooltip) {
    return (
      <Tooltip
        elevation="modal"
        placement="top-start"
        body={
          <div className={css.userTooltipContainer}>
            {userTooltipInfo.map(({label, value}, index) => (
              <div
                key={label || `value-${index}`}
                className={css.userTooltipInfoRow}
              >
                {label ? (
                  <>
                    <BodyMedium color="inversePrimary">{label}:</BodyMedium>
                    <BodyMedium color="inversePrimary">
                      <Truncate>{value ?? '-'}</Truncate>
                    </BodyMedium>
                  </>
                ) : (
                  <BodyMedium color="inversePrimary">
                    <Truncate>{value ?? '-'}</Truncate>
                  </BodyMedium>
                )}
              </div>
            ))}
          </div>
        }
      >
        <SubTitleExtraSmall
          color={semanticColor || 'primary'}
          className={clickable ? css.clickable : css.inlineText}
        >
          {clickable ? `@${displayName}` : displayName}
        </SubTitleExtraSmall>
      </Tooltip>
    );
  } else {
    return (
      <SubTitleExtraSmall
        color={semanticColor || 'primary'}
        className={clickable ? css.clickable : css.inlineText}
      >
        {clickable ? `@${displayName}` : displayName}
      </SubTitleExtraSmall>
    );
  }
};

export default User;
